import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {getIn, useFormikContext} from 'formik';
import {tint} from 'theme/helper';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {Message, useNotificationContext} from 'contexts/NotificationContext';
import {useProductContext} from 'contexts';
import {ProductContextType} from 'components/customer/Product/Specs';
import {validationErrorAppliesToField} from 'shared/validator';
import {
    shortRunner,
    stageHeight,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {hingeVoids} from 'components/customer/Product/Drawer/helpers/innerDrawerGeometry';
import {FreeSpace} from 'components/customer/Product/Drawer/helpers/innerDrawerSlots';
import {useInnerDrawerGeometry} from 'components/customer/Product/Drawer/helpers/useInnerDrawerGeometry';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {DraftInput} from 'components/customer/Product/Drawer/DraftInput';
import {Icon} from 'shared/helpers';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {
    DragLimits,
    DragPreview,
    useInnerDrawerDrag,
} from 'components/customer/Product/Drawer/helpers/useInnerDrawerDrag';
import {
    Badge,
    RAIL_WIDTH,
    Rail,
    RailEnd,
    RailLabel,
    RailLine,
} from 'components/customer/Product/Drawer/layoutStyles';

interface InnerDrawerCarcaseProps {
    drawerParams: DrawerParameters;
    selectedIndex: number;
    onSelect: (index: number) => void;
    // a drag in flight, shared with the panel so the two move together
    preview: DragPreview;
    onPreview: (preview: DragPreview) => void;
    limitsOf: (index: number) => DragLimits;
    // where a drawer can be added, while adding one
    spaces?: FreeSpace[];
    onPlace?: (space: FreeSpace) => void;
    canDelete?: boolean;
    onDelete?: (index: number) => void;
}

// the width every cabinet is drawn at, as the faced drawers are, so the panel
// beside it keeps the same room; folded, it takes the width it is given
const CARCASE_WIDTH = 330;
const LEFT_PAD = RAIL_WIDTH + 4;
// room under the carcase for the width line and its note
const BOTTOM_PAD = 42;
// the break marks' height, centred on the top and bottom panels; short enough
// not to reach a box sitting on the bottom clearance
const BREAK_PIXELS = 16;
// how much wider than drawn the cabinet can be, at the height's scale, and
// still read as fitting, unmarked
const SCALE_TOLERANCE = 0.05;
// a box this short has no room for its caption above the position, so it
// shows the drawer's number beside it instead
const ROOMY_PIXELS = 48;
// a gap this short has no room for its label
const GAP_LABEL_PIXELS = 16;
// keeps a drawer with no runner yet from vanishing
const MIN_BOX_PIXELS = 14;
const NO_SPACES: FreeSpace[] = [];
// the selected box shows its delete only where the icon fits
const DELETE_PIXELS = 24;
// the space between a box and the panels either side of it
const BOX_INSET = 4;

const format = (value: number) => String(Math.round(value * 10) / 10);

// The width is drawn at CARCASE_WIDTH whatever it is. Where that is narrower
// than the height's scale gives, the top, bottom and width line carry the
// drafting break that says it was shortened.
const PANEL_BREAK = (
    <svg
        width="14"
        height={BREAK_PIXELS}
        viewBox="0 0 14 16"
        aria-hidden="true">
        <polygon
            points="4,0 4,5 1,7 7,9 4,11 4,16 10,16 10,11 13,9 7,7 10,5 10,0"
            fill="#fff"
        />
        <polyline
            points="4,0 4,5 1,7 7,9 4,11 4,16"
            fill="none"
            stroke="currentColor"
        />
        <polyline
            points="10,0 10,5 7,7 13,9 10,11 10,16"
            fill="none"
            stroke="currentColor"
        />
    </svg>
);

const LINE_BREAK = (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <rect width="12" height="12" fill="#fff" />
        <polyline
            points="0,6 3,6 5,1 7,11 9,6 12,6"
            fill="none"
            stroke="currentColor"
        />
    </svg>
);

// The cabinet front on: carcase panels, the hinge-side partitions, and each
// drawer box at its position, all to one scale but for the cabinet's width,
// which the boxes stretch to fill. Nothing here works anything out; it draws
// the positions and runners the drawers already hold.
export const InnerDrawerCarcase = ({
    drawerParams,
    selectedIndex,
    onSelect,
    preview,
    onPreview,
    limitsOf,
    spaces = NO_SPACES,
    onPlace,
    canDelete = false,
    onDelete,
}: InnerDrawerCarcaseProps) => {
    const {values, errors} = useFormikContext<DrawerFields>();
    const {messages} = useNotificationContext();
    const {productDataStore} = useProductContext<ProductContextType>();
    const compact = useCompactLayout();
    const {setDraft, commit} = useFaceDraft();

    // px per mm, set by the cabinet's height alone
    const ratio = useMemo(() => {
        const height = toNumber(values.cabinet_height);

        if (!(height > 0)) return 1;

        return stageHeight(height, compact) / height;
    }, [values.cabinet_height, compact]);

    // measured, as a folded card sets it rather than CARCASE_WIDTH
    const [drawnWidth, setDrawnWidth] = useState(0);
    const measure = useCallback((entries: ResizeObserverEntry[]) => {
        setDrawnWidth(entries[0].contentRect.width);
    }, []);
    const panelsRef = useResizeObserver(measure);

    const positionOf = useCallback(
        (index: number) =>
            toNumber(values.drawers?.[Number(index)]?.position_to_bottom),
        [values.drawers]
    );

    // through the drawer's panel field, as a typed position goes
    const commitPosition = useCallback(
        (index: number, position: number) => {
            setDraft(index, String(position));
            commit(index);
        },
        [setDraft, commit]
    );

    // the panel field shows the number as it changes, as it does for typing
    const previewHandler = useCallback(
        (next: DragPreview) => {
            onPreview(next);

            if (next) setDraft(next.index, String(next.position));
        },
        [onPreview, setDraft]
    );

    const geometry = useInnerDrawerGeometry(preview);

    const {startDrag, nudge} = useInnerDrawerDrag({
        ratio,
        positionOf,
        limitsOf,
        onStart: onSelect,
        onPreview: previewHandler,
        commit: commitPosition,
    });

    // the drawer's panel field owns the position and commits what was typed
    const commitHandler = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            const box =
                event.currentTarget.closest<HTMLElement>('[data-index]');

            if (box) commit(Number(box.dataset.index));
        },
        [commit]
    );

    const selectHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            onSelect(Number(event.currentTarget.dataset.index));
        },
        [onSelect]
    );

    const runnerField = drawerParams.drawerRunnerFieldName;
    const attributes = productDataStore?.current?.cabinet?.attributes;

    // the same errors the drawer's own fields show
    const erroredDrawers = useMemo(() => {
        const list: Message[] = Array.isArray(messages?.errors)
            ? messages.errors
            : Object.values(messages?.errors ?? {}).flat<Message[][]>();

        return (values.drawers ?? []).map((_, index) => {
            const position = `drawers[${index}].position_to_bottom`;

            return (
                !!getIn(errors, position) ||
                list.some(
                    (error) =>
                        validationErrorAppliesToField(error, position) ||
                        validationErrorAppliesToField(error, runnerField, index)
                )
            );
        });
    }, [messages, errors, values.drawers, runnerField]);

    const drawing = useMemo(() => {
        if (geometry === null) return null;

        const {height, width, thickness, floor, ceiling, boxes, gaps} =
            geometry;

        const px = (mm: number) => mm * ratio;
        // from the top of the drawing, for a height up from the bottom
        const top = (mm: number) => px(height - mm);
        // from the right of the drawing; each part is placed from its own
        // side, so the boxes stretch to whatever width the drawing has
        const fromRight = (mm: number) => px(width - mm);

        const voids = hingeVoids(
            width,
            thickness,
            toNumber(attributes?.inner_partition_offset),
            String(attributes?.door_hang ?? '')
        );

        // the boxes fit between the side panels, or the partitions
        const left = voids.find((side) => side.side === 'left');
        const right = voids.find((side) => side.side === 'right');
        const across = {
            left: px(left ? left.partitionTo : thickness) + BOX_INSET,
            right:
                fromRight(right ? right.partitionFrom : width - thickness) +
                BOX_INSET,
        };

        return {
            height,
            width,
            pixels: {height: px(height)},
            interior: {
                top: top(ceiling),
                bottom: top(floor),
                left: px(thickness),
                right: px(thickness),
            },
            voids: voids.map((side) => {
                const onLeft = side.side === 'left';

                return {
                    side: side.side,
                    void: onLeft
                        ? {left: px(side.from)}
                        : {right: fromRight(side.to)},
                    width: px(side.to - side.from),
                    partition: onLeft
                        ? {left: px(side.partitionFrom)}
                        : {right: fromRight(side.partitionTo)},
                    partitionWidth: px(side.partitionTo - side.partitionFrom),
                };
            }),
            boxes: boxes.map((box) => {
                const pixels = Math.max(MIN_BOX_PIXELS, px(box.height));

                return {
                    ...box,
                    top: top(box.bottom) - pixels,
                    pixels,
                };
            }),
            across,
            gaps: gaps
                .map((gap) => ({
                    key: `${gap.bottom}-${gap.top}`,
                    size: gap.top - gap.bottom,
                    top: top(gap.top),
                    pixels: px(gap.top - gap.bottom),
                }))
                .filter((gap) => gap.pixels >= GAP_LABEL_PIXELS),
            // the drawer each space would get, where it would go
            spaces: spaces.map((space) => {
                const pixels = Math.max(
                    MIN_BOX_PIXELS,
                    px(space.runner.runnerHeight)
                );

                return {
                    space,
                    top: top(floor + space.position) - pixels,
                    pixels,
                };
            }),
        };
    }, [geometry, attributes, ratio, spaces]);

    // the box would otherwise take the click as selecting it
    const deleteHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onDelete?.(Number(event.currentTarget.dataset.index));
        },
        [onDelete]
    );

    const placeHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const space = spaces[Number(event.currentTarget.dataset.space)];

            if (space) onPlace?.(space);
        },
        [spaces, onPlace]
    );

    if (drawing === null) return null;

    const {pixels, interior, across} = drawing;
    // the cabinet's width at the height's scale against the width drawn
    const widthScale =
        drawnWidth > 0 ? (drawing.width * ratio) / drawnWidth : 1;
    // a narrower cabinet fits, drawn wider than it is, and is left unmarked
    const shortened = widthScale > 1 + SCALE_TOLERANCE;

    return (
        <Stage
            data-cy="inner-drawer-carcase"
            style={{
                width: compact ? undefined : LEFT_PAD + CARCASE_WIDTH,
                height: pixels.height + BOTTOM_PAD,
            }}>
            <Rail
                style={{top: 0, height: pixels.height}}
                data-cy="inner-drawer-height"
                aria-label={`Cabinet height ${format(drawing.height)} mm`}>
                <RailEnd />
                <RailLine />
                <RailLabel>{format(drawing.height)} mm</RailLabel>
                <RailLine />
                <RailEnd />
            </Rail>

            <Panels
                ref={panelsRef}
                style={{
                    left: LEFT_PAD,
                    right: 0,
                    height: pixels.height,
                }}>
                <Interior
                    data-cy="inner-drawer-interior"
                    style={{
                        top: interior.top,
                        left: interior.left,
                        right: interior.right,
                        height: interior.bottom - interior.top,
                    }}
                />

                {/* an open top is broken along the back panel's top edge */}
                {shortened ? (
                    <>
                        <PanelBreak
                            data-cy="inner-drawer-break-top"
                            style={{top: interior.top / 2 - BREAK_PIXELS / 2}}>
                            {PANEL_BREAK}
                        </PanelBreak>
                        <PanelBreak
                            data-cy="inner-drawer-break-bottom"
                            style={{
                                top:
                                    (interior.bottom + pixels.height) / 2 -
                                    BREAK_PIXELS / 2,
                            }}>
                            {PANEL_BREAK}
                        </PanelBreak>
                    </>
                ) : null}

                {drawing.voids.map((side) => (
                    <React.Fragment key={side.side}>
                        <Void
                            data-cy={`inner-drawer-void-${side.side}`}
                            title="Hinge-side void"
                            style={{
                                ...side.void,
                                top: interior.top,
                                width: side.width,
                                height: interior.bottom - interior.top,
                            }}
                        />
                        <Partition
                            data-cy={`inner-drawer-partition-${side.side}`}
                            style={{
                                ...side.partition,
                                top: interior.top,
                                width: side.partitionWidth,
                                height: interior.bottom - interior.top,
                            }}
                        />
                    </React.Fragment>
                ))}

                {drawing.gaps.map((gap) => (
                    <Gap
                        key={gap.key}
                        data-cy="inner-drawer-gap"
                        style={{
                            top: gap.top,
                            right: across.right,
                            height: gap.pixels,
                        }}>
                        ≈{format(gap.size)}
                    </Gap>
                ))}

                {drawing.boxes.map((box) => {
                    const drawer = values.drawers?.[box.index];
                    const runner = shortRunner(
                        drawer?.[runnerField as keyof typeof drawer]
                    );
                    const hasError = erroredDrawers[box.index];
                    const isSelected = box.index === selectedIndex;

                    return (
                        <Box
                            key={box.index}
                            data-index={box.index}
                            data-cy={`inner-drawer-box-${box.index}`}
                            data-error={hasError ? 'true' : 'false'}
                            data-selected={isSelected ? 'true' : 'false'}
                            data-dragging={
                                preview?.index === box.index ? 'true' : 'false'
                            }
                            tabIndex={0}
                            aria-label={`Drawer ${
                                box.index + 1
                            }. Drag it, or use the arrow keys, to move it.`}
                            onClick={selectHandler}
                            onPointerDown={startDrag}
                            onKeyDown={nudge}
                            $error={hasError}
                            $selected={isSelected}
                            $dragging={preview?.index === box.index}
                            title={`Drawer ${box.index + 1}, ${format(
                                box.position
                            )}mm to bottom`}
                            style={{
                                ...across,
                                top: box.top,
                                height: box.pixels,
                            }}>
                            <DragGrip
                                aria-hidden="true"
                                title="Drag to move"
                                data-cy={`inner-drawer-grip-${box.index}`}
                                $selected={isSelected}>
                                <Icon iconName="Drag.svg" />
                            </DragGrip>
                            <Label>
                                {box.pixels >= ROOMY_PIXELS ? (
                                    <Caption>Drawer {box.index + 1}</Caption>
                                ) : null}
                                <Entry>
                                    {box.pixels >= ROOMY_PIXELS ? null : (
                                        <SmallBadge>{box.index + 1}</SmallBadge>
                                    )}
                                    <DraftInput
                                        index={box.index}
                                        value={box.position}
                                        pixels={box.pixels}
                                        dataCy={`inner-drawer-position-${box.index}`}
                                        ariaLabel={`Drawer ${
                                            box.index + 1
                                        } position to bottom in mm`}
                                        onBlur={commitHandler}
                                    />
                                </Entry>
                            </Label>
                            <Spacer />
                            <Runner>
                                {runner === '' ? 'No runner' : runner}
                            </Runner>
                            {canDelete &&
                            isSelected &&
                            box.pixels >= DELETE_PIXELS ? (
                                <BoxDelete
                                    type="button"
                                    title="Remove this drawer"
                                    aria-label={`Remove drawer ${box.index + 1}`}
                                    data-index={box.index}
                                    data-cy={`inner-drawer-remove-${box.index}`}
                                    onClick={deleteHandler}>
                                    <Icon iconName="Options-Delete.svg" />
                                </BoxDelete>
                            ) : null}
                        </Box>
                    );
                })}

                {drawing.spaces.map((ghost, at) => {
                    const runner = shortRunner(ghost.space.runner.id);

                    return (
                        <Space
                            key={`${ghost.space.index}-${ghost.space.position}`}
                            type="button"
                            data-space={at}
                            data-cy={`inner-drawer-space-${ghost.space.index}`}
                            aria-label={`Add a drawer here, at ${format(
                                ghost.space.position
                            )}mm on ${runner}`}
                            title={`Add a drawer at ${format(
                                ghost.space.position
                            )}mm on ${runner}`}
                            onClick={placeHandler}
                            style={{
                                ...across,
                                top: ghost.top,
                                height: ghost.pixels,
                            }}>
                            <Plus aria-hidden="true">
                                <Icon
                                    iconName="Button-Plus.svg"
                                    color="secondary"
                                />
                            </Plus>
                            <SpaceRunner>{runner}</SpaceRunner>
                        </Space>
                    );
                })}
            </Panels>

            <WidthLine
                data-cy="inner-drawer-width"
                style={{
                    top: pixels.height + 8,
                    left: LEFT_PAD,
                    right: 0,
                }}>
                {shortened ? (
                    <LineBreak data-cy="inner-drawer-width-break">
                        {LINE_BREAK}
                    </LineBreak>
                ) : null}
                <span>{format(drawing.width)} mm</span>
            </WidthLine>
            {shortened ? (
                <WidthNote
                    data-cy="inner-drawer-width-note"
                    style={{top: pixels.height + 24, left: LEFT_PAD, right: 0}}>
                    Width not to scale
                </WidthNote>
            ) : null}
        </Stage>
    );
};

const Stage = styled.div`
    position: relative;
    flex: none;
`;

// the carcase panels; the interior is drawn over them, leaving their edges
const Panels = styled.div`
    position: absolute;
    top: 0;
    box-sizing: border-box;
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.35)};
    border-radius: 2px;
`;

// tint() is translucent, and the panels show through it unless it sits on white
const Interior = styled.div`
    position: absolute;
    background:
        linear-gradient(
            ${({theme}) => tint(theme.colors.primary?.main, 0.06)},
            ${({theme}) => tint(theme.colors.primary?.main, 0.06)}
        ),
        #fff;
`;

const Void = styled.div`
    position: absolute;
    background: repeating-linear-gradient(
        45deg,
        ${({theme}) => tint(theme.colors.primary?.main, 0.16)} 0 3px,
        ${({theme}) => tint(theme.colors.primary?.main, 0.04)} 3px 6px
    );
`;

const Partition = styled.div`
    position: absolute;
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.35)};
`;

const Gap = styled.div`
    position: absolute;
    width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: #6b7480;
    font-variant-numeric: tabular-nums;
    pointer-events: none;
`;

// the faced drawing's look: the selected box white, ringed and above the rest
const Box = styled.div<{
    $error: boolean;
    $selected: boolean;
    $dragging: boolean;
}>`
    position: absolute;
    box-sizing: border-box;
    z-index: ${({$selected}) => ($selected ? 2 : 1)};
    /* without this the browser claims the gesture for scrolling on touch */
    touch-action: none;
    outline: none;

    /* the box the arrow keys will move */
    &:focus-visible {
        outline: 2px solid ${({theme}) => theme.colors.secondary?.main};
        outline-offset: 1px;
    }
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    overflow: hidden;
    cursor: ${({$dragging}) => ($dragging ? 'grabbing' : 'grab')};
    border-radius: 2px;
    background: ${({$selected, theme}) =>
        $selected ? '#fff' : tint(theme.colors.primary?.main, 0.05)};
    border: ${({$error, $selected, theme}) =>
        $error
            ? `2px solid ${theme.colors.errorLabel?.main}`
            : $selected
              ? `2px solid ${theme.colors.primary?.main}`
              : `1px solid ${theme.colors.primary?.main}`};
    box-shadow: ${({$error, $selected, theme}) =>
        $error
            ? `inset 0 0 0 1px ${theme.colors.error?.main}`
            : $selected
              ? `0 0 0 3px ${tint(theme.colors.primary?.main, 0.18)}`
              : `inset 0 -1px 0 ${tint(theme.colors.primary?.main, 0.06)}`};
`;

// a drawer that could be added, drawn where it would go
const Space = styled.button`
    position: absolute;
    z-index: 3;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0;
    overflow: hidden;
    cursor: copy;
    border: 1.5px dashed ${({theme}) => theme.colors.secondary?.main};
    border-radius: 3px;
    background: ${({theme}) => tint(theme.colors.secondary?.main, 0.1)};

    &:hover,
    &:focus-visible {
        background: ${({theme}) => tint(theme.colors.secondary?.main, 0.22)};
        outline: none;
    }
`;

// the count's own plus
const Plus = styled.span`
    display: inline-flex;
    width: 22px;
    height: 22px;
    flex: none;

    img {
        width: 100%;
        height: 100%;
    }
`;

const SpaceRunner = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: ${({theme}) => theme.colors.primary?.main};
    background: #fff;
    border-radius: 999px;
    padding: 0 6px;
`;

const Caption = styled.span`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    white-space: nowrap;
    color: #6b7480;
`;

// the same drag icon the job dashboard's rows use; the whole box drags, this
// only shows that it does
const DragGrip = styled.span<{$selected: boolean}>`
    flex: none;
    display: inline-flex;
    margin-left: -4px;
    opacity: ${({$selected}) => ($selected ? 1 : 0.55)};
    cursor: inherit;

    img {
        width: 16px;
        cursor: inherit;
    }
`;

// the caption sits above the position, as on the prototype's boxes
const Label = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const Entry = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SmallBadge = styled(Badge)`
    flex: none;
    width: 18px;
    height: 18px;
    font-size: 11px;
`;

const Spacer = styled.span`
    flex: 1;
`;

const BoxDelete = styled.button`
    flex: none;
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;
    cursor: pointer;

    img {
        width: 20px;
    }
`;

const Runner = styled.span`
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
    color: #8b97a6;
    border-left: 1px solid
        ${({theme}) => tint(theme.colors.primary?.main, 0.15)};
    padding-left: 8px;
    font-variant-numeric: tabular-nums;
`;

const WidthLine = styled.div`
    position: absolute;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid #9aa7b5;
    border-right: 1px solid #9aa7b5;
    box-sizing: border-box;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        border-top: 1px solid #9aa7b5;
    }

    span {
        position: relative;
        padding: 0 6px;
        background: #fff;
        color: #6b7480;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.5px;
        font-variant-numeric: tabular-nums;
    }
`;

// a quarter along, clear of the centred label
const LineBreak = styled.div`
    position: absolute;
    left: 25%;
    top: 0;
    line-height: 0;
    color: #9aa7b5;
    transform: translateX(-50%);
`;

const WidthNote = styled.div`
    position: absolute;
    text-align: center;
    color: #6b7480;
    font-size: 11px;
`;

const PanelBreak = styled.span`
    position: absolute;
    left: 50%;
    line-height: 0;
    color: ${({theme}) => theme.colors.primary?.main};
    transform: translateX(-50%);
    pointer-events: none;
`;
